import { useEffect, useRef, useState } from "react";
import { getDocument, GlobalWorkerOptions, type PDFDocumentProxy, type RenderTask } from "pdfjs-dist";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { apiFetch } from "../../api";
import { getFile, putFile } from "./cache";

GlobalWorkerOptions.workerSrc = workerSrc;

async function loadBytes(url: string, fileId: string): Promise<ArrayBuffer> {
  try {
    const response = await apiFetch(url);
    if (!response.ok) throw new Error("missing");
    const bytes = await response.arrayBuffer();
    const stored = bytes.slice(0);
    void putFile({ id: fileId, mime: "application/pdf", filename: "chart.pdf", bytes: stored, savedAt: new Date().toISOString() }).catch(() => undefined);
    return bytes;
  } catch {
    const cached = await getFile(fileId);
    if (!cached) throw new Error("This PDF is not on this device yet.");
    return cached.bytes.slice(0);
  }
}

export function PdfChart({
  url,
  fileId,
  page,
  zoom,
  night,
  onPages,
}: {
  url: string;
  fileId: string;
  page: number;
  zoom: number;
  night: boolean;
  onPages: (count: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const docRef = useRef<PDFDocumentProxy | null>(null);
  const onPagesRef = useRef(onPages);
  const [message, setMessage] = useState("");
  const [ready, setReady] = useState(0);
  onPagesRef.current = onPages;

  useEffect(() => {
    let cancelled = false;
    setMessage("");
    void loadBytes(url, fileId)
      .then(async (bytes) => {
        const doc = await getDocument({ data: bytes }).promise;
        if (cancelled) {
          await doc.destroy();
          return;
        }
        const previous = docRef.current;
        docRef.current = doc;
        void previous?.destroy();
        onPagesRef.current(doc.numPages);
        setReady((value) => value + 1);
      })
      .catch((caught: Error) => {
        if (!cancelled) setMessage(caught.message || "The PDF could not be drawn.");
      });
    return () => {
      cancelled = true;
    };
  }, [url, fileId]);

  useEffect(() => {
    const doc = docRef.current;
    const canvas = canvasRef.current;
    if (!doc || !canvas) return;
    let cancelled = false;
    let task: RenderTask | null = null;
    const pageNumber = Math.min(Math.max(page, 1), doc.numPages);
    void doc.getPage(pageNumber).then((pdfPage) => {
      if (cancelled) return;
      const viewport = pdfPage.getViewport({ scale: zoom });
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      task = pdfPage.render({ canvas, viewport });
      return task.promise;
    }).catch(() => {
      if (!cancelled) setMessage("That PDF page could not be drawn.");
    });
    return () => {
      cancelled = true;
      try {
        task?.cancel();
      } catch {
        /* the render already finished */
      }
    };
  }, [page, zoom, ready]);

  useEffect(() => () => {
    void docRef.current?.destroy();
    docRef.current = null;
  }, []);

  return (
    <div className="pdf-sheet">
      {message && <p className="notice" role="alert">{message}</p>}
      <canvas ref={canvasRef} className={night ? "page pdf-night" : "page"} aria-label="Chart PDF" />
    </div>
  );
}
